import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { Button, Card, Dropdown, notification } from 'antd';
import type { MenuProps } from 'antd';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import {
  BellOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CommentOutlined,
  DownOutlined,
  InboxOutlined,
  LogoutOutlined,
  MoreOutlined,
  SettingOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { Trans, useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';

import { useAuth } from 'src/util/use-auth';
import InboxNotification from 'src/components/Inbox/InboxNotification';
import { useNotificationReadState } from 'src/components/Inbox/hooks/useNotificationReadState';
import {
  fetchMappedNotifications,
  fetchNotificationUnreadCounts,
  isProgressNotificationType,
  mapApiNotificationItem,
  type FetchNotificationUnreadCountsResult,
  type FetchMappedNotificationsResult,
  type InboxNotificationItem,
} from 'src/util/notificationMapping';
import {
  formatNotificationOriginalDate,
  formatNotificationRelativeTime,
  getNotificationEffectiveDate,
  groupNotificationsByDate,
  isExternalNotificationTarget,
  sortNotificationsByEffectiveDateDesc,
} from 'src/util/notificationFormatting';
import {
  DEFAULT_NOTIFICATION_LABEL,
  getNotificationTypeCategory,
  isNotificationCenterNotificationType,
  type NotificationType,
} from 'src/util/notificationConfig';
import {
  getSettingsKey,
  settingsKeyI18nTitle,
  getSnoozedUntilMs,
  isInAppChannel,
  isNotificationTypeEnabled,
  readNotificationSettings,
  settingsToApiNotificationSettings,
  updateApiNotificationSettings,
  SNOOZE_INDEFINITELY,
  type SnoozeDuration,
} from 'src/util/notificationSettings';
import {
  useNotificationSettings,
  useNotificationSettingsActions,
} from 'src/util/useNotificationSettings';
import { useAPI } from 'src/util/use-api';
import { useWebsocket, WebsocketMessageType } from 'src/util/use-websocket';
import { QUERIES } from 'src/util/globals';
import type { Notification } from 'src/api-sdk';

import { ProfileSwitcher } from './ProfileSwitcher';
import { SwitchProfileModal } from './SwitchProfileModal';

const toTitleCase = (str: string) =>
  str.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const AVATAR_COLORS = [
  ['#1f4f82', '#e8f0fb'],
  ['#166534', '#dcfce7'],
  ['#7c3aed', '#ede9fe'],
  ['#b45309', '#fef3c7'],
  ['#0e7490', '#cffafe'],
  ['#be185d', '#fce7f3'],
];

const getAvatarColor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++)
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]!;
};

const capitalizeFirst = (value: string) =>
  value ? value.charAt(0).toLocaleUpperCase() + value.slice(1) : value;

const getDisplayName = (
  user:
    | {
        first_name?: string;
        last_name?: string;
        username?: string;
        email?: string;
      }
    | null
    | undefined
) =>
  user
    ? `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
      user.username ||
      user.email ||
      ''
    : '';

interface UserAvatarProps {
  /**
   * Desktop only: renders the active-profile indicator next to the bell and
   * routes the account menu's "Switch Profile" to its popover instead of the
   * modal. Mobile keeps the modal, where there is no room for the indicator.
   */
  showProfileSwitcher?: boolean;
}

const UserAvatar = ({ showProfileSwitcher = false }: UserAvatarProps) => {
  const { t } = useTranslation('menu');
  const { user } = useAuth();
  const router = useRouter();
  const { API } = useAPI();
  const queryClient = useQueryClient();
  const notificationSettings = useNotificationSettings();
  const { applyNotificationSettings, refetchNotificationSettings } =
    useNotificationSettingsActions();
  const { registerListener, removeListener } = useWebsocket();
  const [toastApi, toastContextHolder] = notification.useNotification({
    placement: 'bottomLeft',
  });

  const { isRead, syncConfirmedIds, setReadStateForIds } =
    useNotificationReadState({
      api: API,
    });

  const [switchModalVisible, setSwitchModalVisible] = useState(false);
  const [profilePopoverOpen, setProfilePopoverOpen] = useState(false);
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] =
    useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [notificationItems, setNotificationItems] = useState<
    InboxNotificationItem[]
  >([]);
  const [previewUnreadIds, setPreviewUnreadIds] = useState<Set<string>>(
    () => new Set()
  );
  const notificationItemsRef = useRef<InboxNotificationItem[]>([]);
  const previewUnreadIdsRef = useRef<Set<string>>(new Set());
  const pendingReadIdsRef = useRef<Set<string>>(new Set());

  const unreadCountsQueryKey = useMemo(
    () => [QUERIES.INBOX_NOTIFICATIONS, 'unread-counts', null, null, null],
    []
  );
  const { data: unreadCountsData } = useQuery({
    queryKey: unreadCountsQueryKey,
    queryFn: () => fetchNotificationUnreadCounts(API),
    enabled: Boolean(API?.activitiesAPI),
    placeholderData: keepPreviousData,
  });

  // Load notifications and read ids together.
  const fetchNotifications =
    useCallback(async (): Promise<FetchMappedNotificationsResult> => {
      if (!API?.activitiesAPI) {
        return { items: [], confirmedIds: [] };
      }

      try {
        const result = await fetchMappedNotifications(
          API,
          DEFAULT_NOTIFICATION_LABEL
        );

        setNotificationItems(result.items);
        syncConfirmedIds(
          result.items.map((item) => item.id),
          result.confirmedIds
        );
        return result;
      } catch (error) {
        console.error('Failed to fetch notifications', error);
        return { items: [], confirmedIds: [] };
      }
    }, [API, syncConfirmedIds]);

  const isItemUnread = useCallback(
    (
      item: Pick<
        InboxNotificationItem,
        'id' | 'confirmedAt' | 'isUpdatedSinceRead'
      >
    ) => {
      // An item updated after it was read is always unread regardless of the
      // local read cache — the cache may still hold the ID from before the
      // update arrived (e.g. deferred sync while on the inbox page).
      if (item.isUpdatedSinceRead) return true;

      const isReadFromServer = Boolean(item.confirmedAt);

      if (isRead(item.id) || isReadFromServer) {
        return false;
      }

      return true;
    },
    [isRead]
  );

  const groupedNotifications = useMemo(
    () =>
      groupNotificationsByDate(
        sortNotificationsByEffectiveDateDesc(
          notificationItems.filter((item) => {
            if (!isItemUnread(item)) return false;
            if (!notificationSettings.inApp) return false;
            const key = getSettingsKey(item.notificationType);
            return !key || isInAppChannel(notificationSettings[key]);
          })
        )
      ),
    [isItemUnread, notificationItems, notificationSettings]
  );

  const displayedUnreadCount = useMemo(
    () =>
      notificationItems.reduce((count, item) => {
        if (!isItemUnread(item)) return count;
        if (!notificationSettings.inApp) return count;
        const key = getSettingsKey(item.notificationType);
        if (key && !isInAppChannel(notificationSettings[key])) return count;
        return count + 1;
      }, 0),
    [isItemUnread, notificationItems, notificationSettings]
  );

  // Use the server-authoritative total for the badge so it reflects all pages,
  // not just the first page loaded into notificationItems. While the user is on
  // the inbox page, live websocket updates are previewed in the avatar but the
  // inbox statistics stay frozen until "Load updates" is clicked.
  const badgeUnreadCount =
    (unreadCountsData?.byCategory.All ?? displayedUnreadCount) +
    previewUnreadIds.size;

  // The unread badge is visible only when there are unread items, in-app
  // notifications are on, the badge is enabled, and notifications aren't snoozed.
  const hasUnreadBadge =
    badgeUnreadCount !== 0 &&
    notificationSettings.inApp &&
    notificationSettings.showBadge &&
    !(
      notificationSettings.snoozedUntil &&
      Date.now() < notificationSettings.snoozedUntil
    );

  useEffect(() => {
    notificationItemsRef.current = notificationItems;
  }, [notificationItems]);

  useEffect(() => {
    previewUnreadIdsRef.current = previewUnreadIds;
  }, [previewUnreadIds]);

  useEffect(() => {
    setPreviewUnreadIds((current) =>
      current.size === 0 ? current : new Set()
    );
  }, [unreadCountsData?.byCategory.All]);

  const addPreviewUnreadId = useCallback((id: string) => {
    setPreviewUnreadIds((current) => {
      if (current.has(id)) return current;
      const next = new Set(current);
      next.add(id);
      return next;
    });
  }, []);

  const removePreviewUnreadIds = useCallback((ids: string[]) => {
    if (ids.length === 0) return;

    setPreviewUnreadIds((current) => {
      const next = new Set(current);
      let changed = false;
      ids.forEach((id) => {
        if (next.delete(id)) {
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, []);

  const adjustUnreadCounts = useCallback(
    (notificationType: NotificationType, delta: number) => {
      if (delta === 0) return;

      const category = getNotificationTypeCategory(notificationType);
      queryClient.setQueriesData<FetchNotificationUnreadCountsResult>(
        { queryKey: [QUERIES.INBOX_NOTIFICATIONS, 'unread-counts'] },
        (current) => {
          if (!current) return current;

          return {
            byType: {
              ...current.byType,
              [notificationType]: Math.max(
                0,
                (current.byType[notificationType] ?? 0) + delta
              ),
            },
            byCategory: {
              ...current.byCategory,
              All: Math.max(0, current.byCategory.All + delta),
              [category]: Math.max(
                0,
                (current.byCategory[category] ?? 0) + delta
              ),
            },
          };
        }
      );
    },
    [queryClient]
  );

  const markNotificationsReadOptimistically = useCallback(
    (items: InboxNotificationItem[]) => {
      const unreadItems = items.filter(
        (item) => isItemUnread(item) && !pendingReadIdsRef.current.has(item.id)
      );
      const unreadIds = unreadItems.map((item) => item.id);
      const previewUnreadIds = previewUnreadIdsRef.current;

      unreadIds.forEach((id) => pendingReadIdsRef.current.add(id));
      unreadItems.forEach((item) => {
        if (previewUnreadIds.has(item.id)) {
          return;
        }

        adjustUnreadCounts(item.notificationType, -1);
      });
      removePreviewUnreadIds(unreadIds);

      void setReadStateForIds(unreadIds, true).finally(() => {
        unreadIds.forEach((id) => pendingReadIdsRef.current.delete(id));
      });
    },
    [
      adjustUnreadCounts,
      isItemUnread,
      removePreviewUnreadIds,
      setReadStateForIds,
    ]
  );

  // Open the inbox from notification actions.
  const goToInbox = useCallback(() => {
    setIsNotificationDropdownOpen(false);
    router.push('/inbox');
  }, [router]);

  // Route notification actions to internal pages, and mark as read.
  const handleNotificationAction = useCallback(
    (targetUrl?: string | null, item?: InboxNotificationItem) => {
      setIsNotificationDropdownOpen(false);

      if (item) {
        markNotificationsReadOptimistically([item]);
      }

      if (!targetUrl) {
        router.push('/inbox');
        return;
      }

      // External targets (e.g. export download links) open in a new tab;
      // internal app routes keep in-app navigation.
      if (isExternalNotificationTarget(targetUrl)) {
        window.open(targetUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      router.push(targetUrl);
    },
    [markNotificationsReadOptimistically, router]
  );

  const handleBellDropdownOpenChange = useCallback(
    (open: boolean) => {
      setIsNotificationDropdownOpen(open);
      if (open) {
        fetchNotifications();
      }
    },
    [fetchNotifications]
  );

  // Route user menu actions.
  const handleMenuClick = useCallback(
    ({ key }: { key: string }) => {
      const actions: Record<string, () => void> = {
        'switch-profile': () =>
          showProfileSwitcher
            ? setProfilePopoverOpen(true)
            : setSwitchModalVisible(true),
        settings: () => router.push('/account'),
        inbox: () => router.push('/inbox'),
        'support-requests': () => router.push('/feedback/support_requests'),
        'notification-settings': () =>
          router.push('/account/notification-settings'),
        logout: () => router.push('/logout'),
      };

      actions[key]?.();
    },
    [router, showProfileSwitcher]
  );

  const menuItems = useMemo<MenuProps['items']>(
    () => [
      ...(user?.multiple_profiles
        ? [
            {
              key: 'switch-profile',
              icon: <SwapOutlined />,
              label: t('Switch Profile'),
            },
          ]
        : []),
      {
        key: 'settings',
        icon: <SettingOutlined />,
        label: t('Settings'),
      },
      {
        key: 'inbox',
        icon: <InboxOutlined />,
        label: t('Inbox'),
      },
      {
        key: 'support-requests',
        icon: <CommentOutlined />,
        label: t('Support Requests'),
      },
      { type: 'divider' as const },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: t('Logout'),
      },
    ],
    [t, user?.multiple_profiles]
  );

  const displayName = getDisplayName(user);

  const isOnInboxPage = router.pathname.startsWith('/inbox');

  const isActivelySnoozed = useMemo(
    () =>
      Boolean(notificationSettings.snoozedUntil) &&
      Date.now() < (notificationSettings.snoozedUntil ?? 0),
    [notificationSettings.snoozedUntil]
  );

  const bellIcon = useMemo(() => {
    if (!notificationSettings.inApp) {
      return (
        <NotificationsOffIcon
          style={{
            fontSize: 20,
            verticalAlign: 'middle',
            marginRight: '-2px',
            marginBottom: '5px',
          }}
        />
      );
    }
    if (isActivelySnoozed) {
      return (
        <NotificationsOffIcon
          style={{
            fontSize: 20,
            verticalAlign: 'middle',
            marginRight: '-2px',
            marginBottom: '5px',
          }}
        />
      );
    }
    return <BellOutlined style={{ fontSize: 16 }} />;
  }, [notificationSettings.inApp, isActivelySnoozed]);

  const getNotificationActions = useCallback(
    (item: InboxNotificationItem) => {
      const showPrimaryAction = !isOnInboxPage || Boolean(item.targetUrl);
      const showSecondaryAction = !isOnInboxPage;

      return {
        primaryActionLabel: showPrimaryAction
          ? item.primaryActionLabel
          : undefined,
        secondaryActionLabel: showSecondaryAction
          ? item.secondaryActionLabel
          : undefined,
        onPrimaryAction: showPrimaryAction
          ? () => void handleNotificationAction(item.targetUrl, item)
          : undefined,
        onSecondaryAction: showSecondaryAction ? goToInbox : undefined,
      };
    },
    [goToInbox, handleNotificationAction, isOnInboxPage]
  );

  const setNotificationTypeMuted = useCallback(
    (notificationType: string, muted: boolean) => {
      const key = getSettingsKey(
        notificationType as Parameters<typeof getSettingsKey>[0]
      );
      if (!key) return;

      const current = readNotificationSettings();
      const next = { ...current, [key]: muted ? 'off' : 'on' };

      applyNotificationSettings(next);
      void updateApiNotificationSettings(
        API.activitiesAPI,
        settingsToApiNotificationSettings(next)
      )
        .then(() =>
          queryClient.refetchQueries({
            queryKey: [QUERIES.INBOX_NOTIFICATIONS, 'unread-counts'],
            type: 'active',
          })
        )
        .catch(refetchNotificationSettings);
    },
    [API, applyNotificationSettings, queryClient, refetchNotificationSettings]
  );

  const showMuteToast = useCallback(
    (typeLabel: string, onUndo: () => void) => {
      const toastKey = `avatar-mute-${typeLabel}`;
      toastApi.open({
        key: toastKey,
        className: 'inbox-live-toast-notice',
        duration: 5,
        closeIcon: null,
        message: null,
        description: (
          <div className="relative w-full rounded-lg border border-brand-alt bg-brand-blue-100 p-4 text-sm text-brand-text shadow-md">
            <div className="flex gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-none bg-brand-blue-200 text-base text-brand-alt">
                <NotificationsOffIcon style={{ fontSize: 18 }} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="!my-0 text-[13px] font-medium text-brand-alt">
                  {t('Notifications', { ns: 'inbox' })}{' '}
                  <span className="font-normal text-gray-400">
                    • {t('just now', { ns: 'inbox' })}
                  </span>
                </p>
                <p className="!my-0 text-sm text-brand-text">
                  <Trans
                    i18nKey="You have muted <type>{{type}}</type>."
                    ns="inbox"
                    values={{ type: typeLabel }}
                    components={{
                      type: <span className="font-medium text-brand" />,
                    }}
                  />
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  onUndo();
                  toastApi.destroy(toastKey);
                }}
                className="shrink-0 self-center rounded-md border border-brand-blue-300 bg-white px-3 py-1 text-xs font-medium text-brand-alt transition hover:bg-brand-blue-100"
              >
                {t('Undo', { ns: 'inbox' })}
              </button>
            </div>
          </div>
        ),
      });
    },
    [toastApi, t]
  );

  const muteNotificationType = useCallback(
    (item: { notificationType: string; typeLabel: string }) => {
      setNotificationTypeMuted(item.notificationType, true);
      const key = getSettingsKey(
        item.notificationType as Parameters<typeof getSettingsKey>[0]
      );
      const label = key
        ? toTitleCase(t(settingsKeyI18nTitle[key], { ns: 'inbox' }))
        : item.typeLabel;
      showMuteToast(label, () =>
        setNotificationTypeMuted(item.notificationType, false)
      );
    },
    [setNotificationTypeMuted, showMuteToast, t]
  );

  const unmuteNotificationType = useCallback(
    (notificationType: string) =>
      setNotificationTypeMuted(notificationType, false),
    [setNotificationTypeMuted]
  );

  const renderNotification = useCallback(
    (item: InboxNotificationItem) => {
      const actions = getNotificationActions(item);

      return (
        <InboxNotification
          key={item.id}
          priority={item.priority}
          triggerType={item.triggerType}
          typeLabel={item.typeLabel}
          timeAgo={formatNotificationRelativeTime(
            getNotificationEffectiveDate(item)
          )}
          message={item.message}
          quoteText={item.quoteText}
          quoteHighlightValues={item.quoteHighlightValues}
          progress={item.progress}
          progressDetail={item.progressDetail}
          detailCardLabel={item.detailCardLabel}
          detailCardVariant={item.detailCardVariant}
          isUpdated={item.isUpdated}
          originalTimeAgo={
            item.isUpdated
              ? formatNotificationOriginalDate(item.createdAt)
              : undefined
          }
          isRead={!isItemUnread(item)}
          onToggleRead={() => {
            markNotificationsReadOptimistically([item]);
          }}
          primaryActionLabel={actions.primaryActionLabel}
          secondaryActionLabel={actions.secondaryActionLabel}
          onPrimaryAction={actions.onPrimaryAction}
          onSecondaryAction={actions.onSecondaryAction}
          exportFileType={item.exportFileType}
          isMuted={(() => {
            const key = getSettingsKey(item.notificationType);
            return key ? !isInAppChannel(notificationSettings[key]) : false;
          })()}
          muteLabel={(() => {
            const key = getSettingsKey(item.notificationType);
            return key
              ? toTitleCase(t(settingsKeyI18nTitle[key], { ns: 'inbox' }))
              : undefined;
          })()}
          onMuteType={
            getSettingsKey(item.notificationType)
              ? () => muteNotificationType(item)
              : undefined
          }
          onUnmuteType={
            getSettingsKey(item.notificationType)
              ? () => unmuteNotificationType(item.notificationType)
              : undefined
          }
          iconUrl={item.iconUrl}
        />
      );
    },
    [
      getNotificationActions,
      isItemUnread,
      markNotificationsReadOptimistically,
      muteNotificationType,
      notificationSettings,
      t,
      unmuteNotificationType,
    ]
  );

  const showNotificationToast = useCallback(
    (item: InboxNotificationItem) => {
      const toastKey = `notification-${item.id}`;
      const closeToast = () => toastApi.destroy(toastKey);
      const openInboxFromToast = () => {
        closeToast();
        goToInbox();
      };
      // The preview/detail card links to the notification's target (opening
      // external download links in a new tab), while clicking the toast body
      // still opens the inbox.
      const openTargetFromToast = () => {
        closeToast();
        handleNotificationAction(item.targetUrl, item);
      };
      const showCompletionPreview =
        isProgressNotificationType(item.notificationType) &&
        typeof item.progress !== 'number';

      toastApi.open({
        key: toastKey,
        className: 'inbox-live-toast-notice inbox-live-toast-wide',
        duration: 10,
        message: null,
        description: (
          <InboxNotification
            priority={item.priority}
            triggerType={item.triggerType}
            typeLabel={item.typeLabel}
            timeAgo={formatNotificationRelativeTime(
              getNotificationEffectiveDate(item)
            )}
            message={item.message}
            quoteText={item.quoteText}
            quoteHighlightValues={item.quoteHighlightValues}
            progress={item.progress}
            progressDetail={item.progressDetail}
            detailCardLabel={item.detailCardLabel}
            detailCardVariant={item.detailCardVariant}
            isUpdated={item.isUpdated}
            originalTimeAgo={
              item.isUpdated
                ? formatNotificationOriginalDate(item.createdAt)
                : undefined
            }
            isRead={false}
            hideReadToggle
            hideDetailCard={!showCompletionPreview}
            showPrimaryActionWithDetailCard={showCompletionPreview}
            onNotificationClick={openInboxFromToast}
            onPrimaryAction={item.targetUrl ? openTargetFromToast : undefined}
            exportFileType={item.exportFileType}
            iconUrl={item.iconUrl}
          />
        ),
      });
    },
    [goToInbox, handleNotificationAction, toastApi]
  );

  useEffect(() => {
    const listener = {
      key: 'avatar-notification-toast',
      message_type: WebsocketMessageType.NOTIFICATION_UPDATE,
      callback: (msg: { data: { event?: string; notification?: unknown } }) => {
        const raw = msg.data?.notification as
          Record<string, unknown> | null | undefined;
        if (!raw) {
          return;
        }
        if (
          !isNotificationCenterNotificationType(
            raw.notification_type as string | undefined
          )
        ) {
          return;
        }

        const event = msg.data?.event ?? 'created';

        const item = mapApiNotificationItem(
          raw as unknown as Notification,
          DEFAULT_NOTIFICATION_LABEL,
          // While on the inbox page, the inbox handler owns recording the
          // meaningful update (deferred to "Load updates"). Recording it here
          // too would let a refetch flip the inbox item to unread early.
          { rememberMeaningfulUpdate: !isOnInboxPage }
        );
        const previousItem = notificationItemsRef.current.find(
          (currentItem) => currentItem.id === item.id
        );
        const wasUnread = previousItem ? isItemUnread(previousItem) : false;
        const wouldMarkUnread = !item.confirmedAt || item.isUpdatedSinceRead;
        const isPendingLocalRead = pendingReadIdsRef.current.has(item.id);
        const willAppearUnread = wouldMarkUnread && !isPendingLocalRead;
        const shouldDeferUnread = isOnInboxPage && willAppearUnread;

        setNotificationItems((current) => {
          const idx = current.findIndex(
            (currentItem) => currentItem.id === item.id
          );
          if (idx === -1) {
            const next = [item, ...current];
            notificationItemsRef.current = next;
            return next;
          }

          const next = [
            ...current.slice(0, idx),
            item,
            ...current.slice(idx + 1),
          ];
          notificationItemsRef.current = next;
          return next;
        });

        if (!shouldDeferUnread) {
          if (willAppearUnread && !wasUnread) {
            adjustUnreadCounts(item.notificationType, 1);
          } else if (!willAppearUnread && wasUnread) {
            adjustUnreadCounts(item.notificationType, -1);
          }
        } else if (willAppearUnread && !wasUnread) {
          addPreviewUnreadId(item.id);
        }

        if (!shouldDeferUnread) {
          void queryClient.invalidateQueries({
            queryKey: [QUERIES.INBOX_NOTIFICATIONS, 'unread-counts'],
          });
        }

        // Sync the shared read-state for the bell. While the user is on the
        // inbox page, the inbox owns when an updated-since-read notification
        // flips back to unread (it defers that to "Load updates"), so we must
        // not strip the id from readIds here — that would surface unread
        // styling in the inbox immediately. We still re-confirm ids that are
        // read, which never causes a premature unread flip.
        if (!shouldDeferUnread) {
          syncConfirmedIds(
            [item.id],
            willAppearUnread ? [] : [item.id],
            willAppearUnread ? { unreadIds: [item.id] } : undefined
          );
        }

        if (
          event === 'updated' &&
          raw.confirmed_at &&
          !item.isUpdatedSinceRead
        ) {
          return;
        }

        if (
          isOnInboxPage ||
          !isNotificationTypeEnabled(item.notificationType)
        ) {
          return;
        }

        showNotificationToast(item);
      },
    };

    registerListener(listener);
    return () => removeListener(listener);
  }, [
    adjustUnreadCounts,
    addPreviewUnreadId,
    isItemUnread,
    isOnInboxPage,
    queryClient,
    registerListener,
    removeListener,
    showNotificationToast,
    syncConfirmedIds,
  ]);

  return (
    <>
      {toastContextHolder}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          height: '100%',
          marginBottom: 0,
          lineHeight: 1,
          minWidth: 0,
        }}
      >
        {showProfileSwitcher && (
          <>
            <ProfileSwitcher
              open={profilePopoverOpen}
              onOpenChange={setProfilePopoverOpen}
            />
            <span
              style={{
                width: 1,
                height: 24,
                background: '#e8e8e8',
                flex: 'none',
              }}
            />
          </>
        )}

        <div>
          <Dropdown
            placement="bottom"
            trigger={['click']}
            open={isNotificationDropdownOpen}
            onOpenChange={handleBellDropdownOpenChange}
            destroyPopupOnHide
            dropdownRender={() => (
              <Card className="w-[520px] gx-card-widget rounded-md border border-gray-200 bg-white shadow-xl">
                <div className="flex flex-col gap-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="mb-0 text-xl leading-5 text-[#111827]">
                        {t('Notifications', { ns: 'inbox' })}
                      </p>
                      <p className="mb-0 mt-1 text-sm text-gray-400">
                        {displayedUnreadCount > 0
                          ? t('{{count}} unread notification', {
                              ns: 'inbox',
                              count: displayedUnreadCount,
                            })
                          : t('No new notifications', { ns: 'inbox' })}
                      </p>
                    </div>
                    <Dropdown
                      trigger={['click']}
                      placement="bottomRight"
                      menu={{
                        items: [
                          ...(displayedUnreadCount > 0
                            ? [
                                {
                                  key: 'mark-all-read',
                                  icon: <CheckOutlined />,
                                  label: t('Mark all as read', {
                                    ns: 'inbox',
                                  }),
                                },
                              ]
                            : []),
                          isActivelySnoozed
                            ? {
                                key: 'cancel-snooze',
                                icon: <ClockCircleOutlined />,
                                label: t('Cancel snooze', { ns: 'inbox' }),
                              }
                            : {
                                key: 'snooze',
                                icon: <ClockCircleOutlined />,
                                label: t('Snooze notifications', {
                                  ns: 'inbox',
                                }),
                                children: [
                                  {
                                    key: 'snooze-30m',
                                    label: t('For 30 minutes', {
                                      ns: 'inbox',
                                    }),
                                  },
                                  {
                                    key: 'snooze-1h',
                                    label: t('For 1 hour', { ns: 'inbox' }),
                                  },
                                  {
                                    key: 'snooze-4h',
                                    label: t('For 4 hours', { ns: 'inbox' }),
                                  },
                                  {
                                    key: 'snooze-tomorrow',
                                    label: t('Until tomorrow morning', {
                                      ns: 'inbox',
                                    }),
                                  },
                                  {
                                    key: 'snooze-indefinitely',
                                    label: t('Until I turn it off', {
                                      ns: 'inbox',
                                    }),
                                  },
                                ],
                              },
                          ...(!notificationSettings.inApp
                            ? [
                                {
                                  key: 'enable-inapp',
                                  icon: <BellOutlined />,
                                  label: t('Turn on notifications', {
                                    ns: 'inbox',
                                  }),
                                },
                              ]
                            : [
                                {
                                  key: 'disable-inapp',
                                  icon: (
                                    <NotificationsOffIcon
                                      style={{
                                        fontSize: 14,
                                        display: 'block',
                                      }}
                                    />
                                  ),
                                  label: t('Turn off notifications', {
                                    ns: 'inbox',
                                  }),
                                },
                              ]),
                          {
                            key: 'notification-settings',
                            icon: <SettingOutlined />,
                            label: t('Notification settings', {
                              ns: 'inbox',
                            }),
                          },
                        ],
                        onClick: ({ key }) => {
                          if (key === 'mark-all-read') {
                            const unreadItems =
                              notificationItems.filter(isItemUnread);
                            markNotificationsReadOptimistically(unreadItems);
                          } else if (key === 'cancel-snooze') {
                            const current = readNotificationSettings();
                            applyNotificationSettings({
                              ...current,
                              snoozedUntil: null,
                            });
                          } else if (key === 'enable-inapp') {
                            const current = readNotificationSettings();
                            const next = {
                              ...current,
                              inApp: true,
                              snoozedUntil: null,
                            };
                            applyNotificationSettings(next);
                            void updateApiNotificationSettings(
                              API.activitiesAPI,
                              settingsToApiNotificationSettings(next)
                            ).finally(refetchNotificationSettings);
                          } else if (key === 'disable-inapp') {
                            const current = readNotificationSettings();
                            const next = {
                              ...current,
                              inApp: false,
                              snoozedUntil: null,
                            };
                            applyNotificationSettings(next);
                            void updateApiNotificationSettings(
                              API.activitiesAPI,
                              settingsToApiNotificationSettings(next)
                            ).finally(refetchNotificationSettings);
                          } else if (key.startsWith('snooze-')) {
                            const duration = key.replace(
                              'snooze-',
                              ''
                            ) as SnoozeDuration;
                            const current = readNotificationSettings();
                            applyNotificationSettings({
                              ...current,
                              inApp: true,
                              snoozedUntil: getSnoozedUntilMs(duration),
                            });
                          } else if (key === 'notification-settings') {
                            setIsNotificationDropdownOpen(false);
                            router.push('/account/notification-settings');
                          }
                        },
                      }}
                    >
                      <Button
                        type="text"
                        size="small"
                        aria-label={t('More options')}
                        className="!h-7 !w-7 !rounded-md !text-gray-500 hover:!bg-gray-100 hover:!text-gray-600 shrink-0"
                        icon={<MoreOutlined />}
                      />
                    </Dropdown>
                  </div>

                  {(() => {
                    const snoozedUntil = notificationSettings.snoozedUntil;
                    const isSnoozed =
                      Boolean(snoozedUntil) && Date.now() < (snoozedUntil ?? 0);
                    const isDisabled = !notificationSettings.inApp;
                    const isVisible = isSnoozed || isDisabled;

                    const label = isDisabled
                      ? t('Notifications turned off', { ns: 'inbox' })
                      : snoozedUntil === SNOOZE_INDEFINITELY
                        ? t('Snoozed indefinitely', { ns: 'inbox' })
                        : t('Snoozed until {{time}}', {
                            ns: 'inbox',
                            time: new Date(snoozedUntil!).toLocaleTimeString(
                              [],
                              {
                                hour: '2-digit',
                                minute: '2-digit',
                              }
                            ),
                          });

                    const actionLabel = isDisabled
                      ? t('Turn on', { ns: 'inbox' })
                      : t('Cancel', { ns: 'inbox' });

                    const handleAction = () => {
                      const current = readNotificationSettings();
                      const next = isDisabled
                        ? { ...current, inApp: true }
                        : { ...current, snoozedUntil: null };
                      applyNotificationSettings(next);
                      if (isDisabled) {
                        void updateApiNotificationSettings(
                          API.activitiesAPI,
                          settingsToApiNotificationSettings(next)
                        ).catch(refetchNotificationSettings);
                      }
                    };

                    return (
                      <div
                        className={`overflow-hidden transition-all duration-200 ${
                          isVisible
                            ? 'max-h-20 opacity-100'
                            : 'max-h-0 opacity-0'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                          <div className="flex items-center gap-2 text-sm text-amber-700">
                            <NotificationsOffIcon style={{ fontSize: 16 }} />
                            <span>{label}</span>
                          </div>
                          <button
                            type="button"
                            className="text-xs text-amber-700 hover:underline shrink-0"
                            onClick={handleAction}
                          >
                            {actionLabel}
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="max-h-[420px] overflow-y-auto">
                    {groupedNotifications.length > 0 ? (
                      <div className="space-y-4">
                        {groupedNotifications.map(([groupLabel, items]) => {
                          const unreadGroupCount =
                            items.filter(isItemUnread).length;
                          const allGroupRead = unreadGroupCount === 0;
                          return (
                            <div key={groupLabel}>
                              <div className="mb-2 flex items-center justify-between text-sm text-gray-500">
                                <span>{capitalizeFirst(groupLabel)}</span>
                                <button
                                  type="button"
                                  disabled={allGroupRead}
                                  className={`inline-flex !h-7 items-center gap-1.5 text-xs ${
                                    allGroupRead
                                      ? 'cursor-default text-brand-gray'
                                      : '!rounded-md !px-2 !text-brand-alt hover:!bg-brand-blue-100'
                                  }`}
                                  onClick={() => {
                                    if (allGroupRead) return;
                                    markNotificationsReadOptimistically(items);
                                  }}
                                >
                                  <span>
                                    {allGroupRead
                                      ? t('All read')
                                      : t('Mark all as read')}
                                  </span>
                                  {unreadGroupCount > 0 ? (
                                    <span className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-brand-blue-200 px-1.5 text-[10px] font-medium leading-4 text-brand-alt">
                                      {unreadGroupCount}
                                    </span>
                                  ) : null}
                                </button>
                              </div>
                              <div className="space-y-2">
                                {items.map(renderNotification)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex h-[220px] flex-col items-center justify-center gap-2 text-gray-300">
                        <BellOutlined style={{ fontSize: 32 }} />
                        <span className="text-sm font-normal">
                          {t("You're all caught up", { ns: 'inbox' })}
                        </span>
                      </div>
                    )}
                  </div>

                  {!isOnInboxPage ? (
                    <button
                      type="button"
                      onClick={goToInbox}
                      className="w-full rounded-md border border-gray-200 bg-white px-4 py-2 text-sm text-[#1f4f82] cursor-pointer transition-colors hover:bg-brand-blue-100 hover:border-brand-alt"
                    >
                      {t('See all notifications', { ns: 'inbox' })}
                    </button>
                  ) : null}
                </div>
              </Card>
            )}
          >
            <Button
              type="text"
              icon={bellIcon}
              aria-label={t('Alerts')}
              className={`transition-colors hover:!text-[#595959] ${
                hasUnreadBadge ? '!text-[#262626]' : '!text-[#B2B2B2]'
              }`}
              style={{
                padding: '6px 8px',
                height: 'auto',
                marginBottom: 0,
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
              }}
            >
              {hasUnreadBadge && (
                <span
                  style={{
                    position: 'absolute',
                    top: 3,
                    right: 4,
                    background: '#D52B1E',
                    color: '#fff',
                    borderRadius: 999,
                    fontSize: 10,
                    lineHeight: '14px',
                    minWidth: 14,
                    height: 14,
                    textAlign: 'center',
                    padding: '0 3px',
                  }}
                >
                  {badgeUnreadCount}
                </span>
              )}
            </Button>
          </Dropdown>
        </div>

        <Dropdown
          menu={{ items: menuItems, onClick: handleMenuClick }}
          placement="bottomRight"
          trigger={['click']}
          onOpenChange={setIsUserMenuOpen}
        >
          {(() => {
            const [fg, bg] = getAvatarColor(displayName || '?');
            return (
              <Button
                type="text"
                aria-label="User menu"
                className="gx-user-nav__trigger"
                style={{
                  padding: '6px 8px',
                  height: 'auto',
                  marginBottom: 0,
                  color: '#262626',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: bg,
                    color: fg,
                    fontSize: 10,
                    fontWeight: 600,
                    flexShrink: 0,
                    userSelect: 'none',
                  }}
                >
                  {getInitials(displayName)}
                </span>
                <span
                  className="gx-user-nav__name"
                  title={displayName}
                  style={{ fontWeight: 500, fontSize: '14px' }}
                >
                  {displayName}
                </span>
                <DownOutlined
                  style={{
                    fontSize: '10px',
                    color: '#8c8c8c',
                    marginLeft: '0px',
                    transition: 'transform 200ms ease',
                    transform: isUserMenuOpen
                      ? 'rotate(180deg)'
                      : 'rotate(0deg)',
                  }}
                />
              </Button>
            );
          })()}
        </Dropdown>
      </div>

      <SwitchProfileModal
        visible={switchModalVisible}
        onClose={() => setSwitchModalVisible(false)}
      />
    </>
  );
};

export default UserAvatar;
